import { useEffect, useState, useCallback, useRef } from 'react';

import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { flushSync } from 'react-dom';

export function useFetchModal<T>(
  url: string,
  isOpen: boolean,
  params?: any
): {
  data: T | null;
  loading: boolean;
  error: Error | null;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Tracks which url is the most recently requested one, so a response for a
  // url we've since moved on from (e.g. the modal was reopened for a
  // different candidate before the previous fetch resolved) gets discarded
  // instead of overwriting state with stale data.
  const requestedUrlRef = useRef<string | null>(null);

  const makeRequest = useCallback(async () => {
    requestedUrlRef.current = url;
    try {
      flushSync(() => {
        setLoading(true);
        setData(null);
      });
      const response = await standardRequestWithErrorFlash.get(url, params);
      if (requestedUrlRef.current !== url) {
        return;
      }
      setData(response.data);
    } catch (e: any) {
      if (requestedUrlRef.current !== url) {
        return;
      }
      setError(e);
    } finally {
      if (requestedUrlRef.current === url) {
        setLoading(false);
      }
    }
  }, [url]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    makeRequest();
  }, [isOpen, url, makeRequest]);
  return { data, loading, error };
}
